import React from 'react';
import { AlertTriangle, CheckCircle2, Gauge, Info, Loader2 } from 'lucide-react';

const getTone = (capacity) => {
  if (!capacity) return 'neutral';
  if (capacity.seuil_atteint) return 'danger';
  if (capacity.proche_seuil) return 'warning';
  return 'success';
};

const toneStyles = {
  success: {
    container: 'border-emerald-200 bg-emerald-50',
    iconBox: 'bg-emerald-100 text-emerald-700',
    title: 'text-emerald-950',
    text: 'text-emerald-700',
    bar: 'bg-emerald-500'
  },
  warning: {
    container: 'border-amber-200 bg-amber-50',
    iconBox: 'bg-amber-100 text-amber-700',
    title: 'text-amber-950',
    text: 'text-amber-700',
    bar: 'bg-amber-500'
  },
  danger: {
    container: 'border-red-200 bg-red-50',
    iconBox: 'bg-red-100 text-red-700',
    title: 'text-red-950',
    text: 'text-red-700',
    bar: 'bg-red-500'
  },
  neutral: {
    container: 'border-gray-200 bg-gray-50',
    iconBox: 'bg-gray-100 text-gray-600',
    title: 'text-gray-900',
    text: 'text-gray-600',
    bar: 'bg-gray-400'
  }
};

const getIcon = (tone) => {
  if (tone === 'danger') return AlertTriangle;
  if (tone === 'success') return CheckCircle2;
  return Info;
};

const ClassCapacityInfo = ({ capacity, loading = false, className = '' }) => {
  if (loading) {
    return (
      <div className={`mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 ${className}`}>
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin text-[#246990]" />
          Chargement du seuil de la classe...
        </div>
      </div>
    );
  }

  if (!capacity) return null;

  const tone = getTone(capacity);
  const styles = toneStyles[tone] || toneStyles.neutral;
  const Icon = getIcon(tone);
  const effectif = Number(capacity.effectif_actuel || 0);
  const seuil = Number(capacity.seuil_effectif || 0);
  const places = Number(capacity.places_disponibles || 0);
  const percent = Math.max(0, Math.min(Number(capacity.taux_remplissage || 0), 100));
  const sourceLabel = capacity.seuil_source === 'classe' ? 'Seuil classe' : 'Seuil par défaut';

  return (
    <div className={`mt-3 rounded-lg border p-3 ${styles.container} ${className}`}>
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${styles.iconBox}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className={`text-sm font-semibold ${styles.title}`}>Capacité de la classe</p>
            <span className={`inline-flex items-center gap-1 rounded-full bg-white/80 px-2 py-1 text-xs font-semibold ${styles.text}`}>
              <Gauge className="h-3.5 w-3.5" />
              {effectif} / {seuil || '-'}
            </span>
          </div>

          <div className="mt-3 h-2 rounded-full bg-white/80 overflow-hidden">
            <div className={`h-full rounded-full ${styles.bar}`} style={{ width: `${percent}%` }} />
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className={styles.text}>
              {capacity.seuil_atteint
                ? 'Seuil atteint'
                : `${places} place${places > 1 ? 's' : ''} disponible${places > 1 ? 's' : ''}`}
            </span>
            <span className="text-gray-600">
              {sourceLabel} : {seuil || '-'} élèves
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassCapacityInfo;
